import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  apercuNouveauMessage,
  envoyerNouveauMessage,
  enregistrerBrouillon,
  getBrouillonLibre,
  getPieceBrouillon,
  remplacerPiecesBrouillon,
} from '../../lib/api/messagerieApi';
import { useCompositionMessage } from '../../lib/messagerie/useCompositionMessage';
import useContactsEquipe from '../../lib/messagerie/useContactsEquipe';
import useRechercheDestinataires from '../../lib/messagerie/useRechercheDestinataires';
import BarreOutilsTexte from './BarreOutilsTexte';
import BlocPieces from './BlocPieces';
import ChampAdresses, { adressesValides, lireAdresses } from './ChampAdresses';
import SectionMail from './SectionMail';
import './Messagerie.css';

const REGEX_MAIL = /^\S+@\S+\.\S+$/;

/** Le poids total autorisé des images et documents d'un message. */
const POIDS_MAX = 5 * 1024 * 1024;

const VIDE = JSON.stringify({ destinataire: '', cc: [], cci: [], sujet: '', titre: '', texte: '', avecPieces: false });

const clePieces = (imgs, docs) =>
  JSON.stringify([...imgs.map((f) => ['I', f.name, f.size]), ...docs.map((f) => ['D', f.name, f.size])]);

/**
 * « ÉCRIRE UN NOUVEAU MESSAGE » — depuis la boîte du support, à une seule
 * adresse (plus des copies), dans la mise en page du site et avec la signature
 * de l'administrateur qui écrit.
 *
 * LE CHAMP MAIL RESTE UN CHAMP, LA RECHERCHE N'EST QU'UN RACCOURCI : les
 * utilisateurs du site proposés sous le champ remplissent l'adresse, mais on
 * peut toujours l'écrire ou la corriger à la main.
 *
 * LE BROUILLON : tant qu'il n'est pas envoyé, le message (adresse, copies, objet,
 * titre, texte) est enregistré au fil de la frappe dans « Brouillons », d'où un
 * clic le rouvre ici ; ses images et documents sont envoyés au serveur à chaque
 * ajout ou retrait, et effacés quand le message part.
 *
 * DEUX CARTES — le destinataire, le courriel — et une barre d'actions qui reste
 * à portée : les deux questions qu'on se pose dans cet ordre.
 */
export default function NouveauMessage({ adresseBoite, brouillonAReprendre = null, onBrouillonRepris = null }) {
  const [destinataire, setDestinataire] = useState('');
  const [listeOuverte, setListeOuverte] = useState(false);
  const [suggestionActive, setSuggestionActive] = useState(-1);
  // LES COPIES ET COPIES CACHÉES, « comme sur Gmail » : un clic sur « Cc » ou
  // « Cci » ouvre leur champ.
  const [copies, setCopies] = useState([]);
  const [cachees, setCachees] = useState([]);
  const [ccVisible, setCcVisible] = useState(false);
  const [cciVisible, setCciVisible] = useState(false);
  const equipe = useContactsEquipe();

  const [sujet, setSujet] = useState('');
  const [titre, setTitre] = useState('');
  const {
    texte, setTexte,
    images, setImages,
    documents, setDocuments,
    zoneTexte,
    insererMarqueur, insererEmoji, insererGras,
    charger: chargerComposition, retirerImage, retirerDocument,
  } = useCompositionMessage();
  const [erreurPiece, setErreurPiece] = useState(null);

  const [confirme, setConfirme] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [envoye, setEnvoye] = useState(null);
  // La confirmation s'efface seule : elle a dit ce qu'elle avait à dire.
  useEffect(() => {
    if (!envoye) return undefined;
    const minuteur = setTimeout(() => setEnvoye(null), 10000);
    return () => clearTimeout(minuteur);
  }, [envoye]);

  // ------------------------------------------------------------ brouillon
  const brouillonId = useRef(null);
  const dernierBrouillon = useRef(VIDE);
  const enAttente = useRef(null);
  const [etatBrouillon, setEtatBrouillon] = useState(null);

  const sauverBrouillon = useCallback(async (contenu) => {
    enAttente.current = null;
    const cle = JSON.stringify(contenu);
    if (cle === dernierBrouillon.current) return;
    dernierBrouillon.current = cle;

    setEtatBrouillon({ etat: 'enregistrement' });
    try {
      const { status, data } = await enregistrerBrouillon({ messageOrigine: null, id: brouillonId.current, ...contenu });
      brouillonId.current = status === 204 ? null : data?.id ?? brouillonId.current;
      setEtatBrouillon(status === 204 ? null : { etat: 'enregistre', heure: new Date() });
    } catch (e) {
      dernierBrouillon.current = null;
      setEtatBrouillon({ etat: 'erreur', message: e?.response?.data?.message });
    }
  }, []);

  /** Ce qui attendait la pause de la frappe part tout de suite. */
  const sauverMaintenant = useCallback(
    () => (enAttente.current ? sauverBrouillon(enAttente.current) : Promise.resolve()),
    [sauverBrouillon],
  );

  // LES IMAGES ET DOCUMENTS DU BROUILLON : envoyés au serveur (rangés sur le
  // disque, pas en base) à chaque ajout ou retrait.
  const dernieresPieces = useRef(clePieces([], []));
  const actuel = useRef({});
  actuel.current = {
    contenu: { destinataire, cc: copies, cci: cachees, sujet, titre, texte, avecPieces: images.length + documents.length > 0 },
    images,
    documents,
  };

  const sauverPieces = useCallback(async (imgs, docs) => {
    const cle = clePieces(imgs, docs);
    if (cle === dernieresPieces.current) return;

    // Il faut un brouillon pour y ranger des pièces : le texte d'abord.
    if (!brouillonId.current) await sauverBrouillon(actuel.current.contenu);
    if (!brouillonId.current) return;

    dernieresPieces.current = cle;
    setEtatBrouillon({ etat: 'enregistrement' });
    try {
      await remplacerPiecesBrouillon(brouillonId.current, imgs, docs);
      setEtatBrouillon({ etat: 'enregistre', heure: new Date() });
    } catch (e) {
      dernieresPieces.current = null;
      setEtatBrouillon({ etat: 'erreur', message: e?.response?.data?.message });
    }
  }, [sauverBrouillon]);

  useEffect(() => {
    if (clePieces(images, documents) === dernieresPieces.current) return undefined;

    const minuteur = setTimeout(() => sauverPieces(images, documents), 600);
    return () => clearTimeout(minuteur);
  }, [images, documents, sauverPieces]);

  /** Le message suivant repart d'un brouillon neuf ; le précédent reste dans « Brouillons ». */
  const nouveauBrouillon = useCallback(async () => {
    await sauverMaintenant();
    await sauverPieces(actuel.current.images, actuel.current.documents);
    brouillonId.current = null;
    dernierBrouillon.current = VIDE;
    dernieresPieces.current = clePieces([], []);
    setEtatBrouillon(null);
  }, [sauverMaintenant, sauverPieces]);

  useEffect(() => {
    const contenu = { destinataire, cc: copies, cci: cachees, sujet, titre, texte, avecPieces: images.length + documents.length > 0 };
    if (JSON.stringify(contenu) === dernierBrouillon.current) return undefined;

    const vide = !destinataire.trim() && copies.length === 0 && cachees.length === 0
      && !sujet.trim() && !titre.trim() && !texte.trim() && !contenu.avecPieces;
    if (vide && !brouillonId.current) return undefined;

    enAttente.current = contenu;
    const minuteur = setTimeout(() => sauverBrouillon(contenu), 1500);
    return () => clearTimeout(minuteur);
  }, [destinataire, copies, cachees, sujet, titre, texte, images, documents, sauverBrouillon]);

  // En fermant la fenêtre, ce qui restait à enregistrer part quand même.
  const sauverRef = useRef(null);
  sauverRef.current = async () => {
    await sauverMaintenant();
    await sauverPieces(actuel.current.images, actuel.current.documents);
  };
  useEffect(() => () => { sauverRef.current(); }, []);

  // UN BROUILLON CHOISI DANS « BROUILLONS » se rouvre ici.
  useEffect(() => {
    if (!brouillonAReprendre?.identifiant) return undefined;
    let annule = false;

    (async () => {
      try {
        const { data } = await getBrouillonLibre(brouillonAReprendre.identifiant);
        if (annule) return;
        await nouveauBrouillon();

        // Les pièces reviennent en vrais fichiers, dans leur ordre : l'envoi les reprend telles quelles.
        const pieces = [...(data.pieces ?? [])].sort((a, b) => (a.rang ?? 0) - (b.rang ?? 0));
        const fichiers = await Promise.all(pieces.map(async (p) => {
          const { data: octets } = await getPieceBrouillon(data.id, p.id);
          return { genre: String(p.genre ?? '').toLowerCase(), fichier: new File([octets], p.nomFichier, { type: p.typeMime }) };
        }));
        if (annule) return;
        const imgs = fichiers.filter((f) => f.genre === 'image').map((f) => f.fichier);
        const docs = fichiers.filter((f) => f.genre !== 'image').map((f) => f.fichier);

        const contenu = {
          destinataire: data.destinataire ?? '',
          cc: lireAdresses(data.copies ?? data.cc),
          cci: lireAdresses(data.copiesCachees ?? data.cci),
          sujet: data.sujet ?? '',
          titre: data.titre ?? '',
          texte: data.texte ?? '',
          avecPieces: imgs.length + docs.length > 0,
        };
        brouillonId.current = data.id;
        dernierBrouillon.current = JSON.stringify(contenu);
        dernieresPieces.current = clePieces(imgs, docs);
        setDestinataire(contenu.destinataire);
        setCopies(contenu.cc);
        setCachees(contenu.cci);
        setCcVisible(contenu.cc.length > 0);
        setCciVisible(contenu.cci.length > 0);
        setSujet(contenu.sujet);
        setTitre(contenu.titre);
        chargerComposition({ texte: contenu.texte, images: imgs, documents: docs });
        setConfirme(false);
        setEtatBrouillon({ etat: 'enregistre', heure: data.dateModification ? new Date(data.dateModification) : new Date() });
      } catch {
        if (!annule) setErreur("Ce brouillon n'a pas pu être rouvert. Il a peut-être été supprimé.");
      } finally {
        if (!annule) onBrouillonRepris?.();
      }
    })();

    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brouillonAReprendre]);

  // ------------------------------------------------------------ pièces
  const poidsTotal = [...images, ...documents].reduce((n, f) => n + (f.size ?? 0), 0);

  const ajouterPieces = (genre, fichiers) => {
    setErreurPiece(null);
    const ajout = fichiers.reduce((n, f) => n + (f.size ?? 0), 0);
    if (poidsTotal + ajout > POIDS_MAX) {
      setErreurPiece('Les pièces dépasseraient 5 Mo au total : retirez-en ou choisissez des fichiers plus légers.');
      return;
    }
    const setListe = genre === 'Image' ? setImages : setDocuments;
    setListe((liste) => [...liste, ...fichiers]);
  };

  const retirerPiece = (genre, index) => {
    setErreurPiece(null);
    if (genre === 'Image') retirerImage(index);
    else retirerDocument(index);
  };

  // ------------------------------------------------------------ destinataire
  const resultats = useRechercheDestinataires(destinataire);
  const propositions = useMemo(() => {
    const terme = destinataire.trim().toLowerCase();
    if (!terme) return [];
    const vues = new Set();
    return [...equipe, ...resultats]
      .filter((p) => p.mail && `${p.nom ?? ''} ${p.mail}`.toLowerCase().includes(terme))
      .filter((p) => {
        const cle = p.mail.toLowerCase();
        if (vues.has(cle) || cle === terme) return false;
        vues.add(cle);
        return true;
      })
      .slice(0, 8);
  }, [destinataire, equipe, resultats]);

  const choisirDestinataire = (p) => {
    setDestinataire(p.mail ?? '');
    setListeOuverte(false);
    setSuggestionActive(-1);
  };

  const listeVisible = listeOuverte && propositions.length > 0;

  const composition = { sujet, titre: titre || sujet, texte, images, documents };
  const adresseValide = REGEX_MAIL.test(destinataire.trim());
  const contenuPret = sujet.trim().length > 0 && texte.trim().length > 0;
  const pret = adresseValide && contenuPret && adressesValides(copies) && adressesValides(cachees);
  const nombreCopies = copies.length + cachees.length;

  const voirApercu = async () => {
    setErreur(null);

    // Ouverte tout de suite, dans le geste du clic : un onglet ouvert après une
    // requête serait bloqué par le navigateur.
    const onglet = window.open('', '_blank');
    try {
      const { data: html } = await apercuNouveauMessage({
        ...composition, destinataire: destinataire.trim(), cc: copies, cci: cachees,
      });
      // Un onglet plutôt qu'une modale, pour voir le courriel à sa taille réelle.
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      if (onglet) onglet.location.href = url;
      else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      onglet?.close();
      setErreur("L'aperçu n'a pas pu être généré.");
    }
  };

  const envoyer = async () => {
    setEnvoi(true);
    setErreur(null);
    setEnvoye(null);

    // Le brouillon en attente ne doit pas renaître après l'envoi.
    enAttente.current = null;
    const brouillonEnvoye = brouillonId.current;

    try {
      const adresse = destinataire.trim();
      const { data } = await envoyerNouveauMessage({
        ...composition, destinataire: adresse, cc: copies, cci: cachees, brouillonId: brouillonEnvoye,
      });

      // Le message est parti : le serveur a effacé son brouillon.
      brouillonId.current = null;
      dernierBrouillon.current = VIDE;
      dernieresPieces.current = clePieces([], []);
      setEtatBrouillon(null);

      setEnvoye({ adresse, copieDeposee: data?.copieDeposee });
      setConfirme(false);
      setDestinataire('');
      setCopies([]);
      setCachees([]);
      setCcVisible(false);
      setCciVisible(false);
      setSujet('');
      setTitre('');
      chargerComposition();
    } catch (e) {
      setErreur(e?.response?.data?.message ?? e?.response?.data?.detail ?? "Le message n'a pas pu être envoyé.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="atelier-mails">
      <div className="message-nouveau">
        {/* L'EXPÉDITEUR : toujours la boîte commune du support. */}
        <div className="messagerie__barre carte-expediteur">
          <div className="messagerie__compte">
            <span className="messagerie__compte-icone" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2.5" />
                <path d="M3.5 7l8.5 6 8.5-6" />
              </svg>
            </span>
            <span className="messagerie__compte-texte">
              <span className="messagerie__compte-libelle">Envoyé depuis</span>
              <strong className="messagerie__compte-adresse">{adresseBoite || 'support@salatjanaza.org'}</strong>
              <span className="carte-expediteur__aide">Avec votre signature, aux couleurs de Salat Janaza.</span>
            </span>
          </div>
        </div>

        {/* ① LE DESTINATAIRE — qui va le recevoir. */}
        <SectionMail
          numero="1"
          titre="Le destinataire"
          aide="Tapez son adresse, ou son nom pour retrouver un utilisateur du site."
          teinte="sauge"
        >
          <div className="champ champ--recherche-destinataire">
            <label htmlFor="nm-mail">Adresse e-mail du destinataire</label>
            {/* CC ET CCI DANS LE CHAMP, comme dans la réponse. */}
            <div className={`champ-avec-copies${ccVisible && cciVisible ? '' : ' avec-liens'}`}>
              <input
                id="nm-mail"
                type="email"
                autoComplete="off"
                role="combobox"
                aria-expanded={listeVisible}
                aria-controls="nm-mail-suggestions"
                aria-autocomplete="list"
                aria-activedescendant={listeVisible && suggestionActive >= 0 ? `nm-mail-suggestions-${suggestionActive}` : undefined}
                value={destinataire}
                onChange={(e) => { setDestinataire(e.target.value); setListeOuverte(true); setSuggestionActive(-1); }}
                onFocus={() => setListeOuverte(true)}
                onKeyDown={(e) => {
                  if (!listeVisible) return;
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSuggestionActive((n) => (n + 1) % propositions.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSuggestionActive((n) => (n <= 0 ? propositions.length - 1 : n - 1));
                  } else if (e.key === 'Enter' && suggestionActive >= 0) {
                    e.preventDefault();
                    choisirDestinataire(propositions[suggestionActive]);
                  } else if (e.key === 'Escape') {
                    e.stopPropagation();
                    setListeOuverte(false);
                  }
                }}
                // Le délai laisse le clic sur une ligne se produire avant que la liste ne se referme.
                onBlur={() => setTimeout(() => setListeOuverte(false), 150)}
                placeholder="nom@exemple.fr"
              />
              {(!ccVisible || !cciVisible) && (
                <span className="champ-avec-copies__liens">
                  {!ccVisible && <button type="button" onClick={() => setCcVisible(true)}>Cc</button>}
                  {!cciVisible && <button type="button" onClick={() => setCciVisible(true)}>Cci</button>}
                </span>
              )}
            </div>

            {listeVisible && (
              <ul className="champ-adresses__suggestions" id="nm-mail-suggestions" role="listbox">
                {propositions.map((p, i) => (
                  <li key={p.mail} id={`nm-mail-suggestions-${i}`} role="option" aria-selected={i === suggestionActive}>
                    <button
                      type="button"
                      tabIndex={-1}
                      className={i === suggestionActive ? 'est-active' : ''}
                      onMouseDown={(e) => { e.preventDefault(); choisirDestinataire(p); }}
                    >
                      <span className="champ-adresses__nom">
                        {p.nom || p.mail}
                        {p.equipe && <span className="champ-adresses__equipe">Équipe</span>}
                      </span>
                      {p.nom && <span className="champ-adresses__mail">{p.mail}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* CC ET CCI, COMME SUR GMAIL : ouverts depuis le champ du destinataire. */}
          {ccVisible && (
            <ChampAdresses
              id="nm-cc"
              libelle="Cc — en copie"
              valeur={copies}
              onChange={setCopies}
              suggestions={equipe}
              autoFocus={copies.length === 0}
            />
          )}
          {cciVisible && (
            <ChampAdresses
              id="nm-cci"
              libelle="Cci — en copie cachée"
              valeur={cachees}
              onChange={setCachees}
              suggestions={equipe}
              autoFocus={cachees.length === 0}
              placeholder="Les autres destinataires ne la verront pas"
            />
          )}
        </SectionMail>

        {/* ② LE COURRIEL — ce que la personne reçoit. */}
        <SectionMail
          numero="2"
          titre="Le courriel"
          aide="Ce que la personne reçoit : l’objet, le message, et ses images ou documents."
          teinte="sable"
        >
          <div className="champ">
            <label htmlFor="nm-sujet">Objet du courriel</label>
            <input
              id="nm-sujet"
              maxLength={150}
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              placeholder="Votre message à Salat Janaza"
            />
          </div>

          <div className="champ">
            <label htmlFor="nm-titre">Titre affiché dans le message</label>
            <input
              id="nm-titre"
              maxLength={150}
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="(identique à l’objet si vous le laissez vide)"
            />
          </div>

          <div className="champ">
            <label htmlFor="nm-texte">Message</label>
            <BarreOutilsTexte onGras={insererGras} onEmoji={insererEmoji} />
            <textarea
              id="nm-texte"
              ref={zoneTexte}
              rows={12}
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder={'Assalamou alaykoum,\n\nUne ligne vide sépare deux paragraphes.\n\nL’équipe Salat Janaza'}
            />
            <span className="champ__aide">
              Une ligne vide crée un paragraphe. <strong>**Ainsi**</strong> devient du gras.
              Une adresse comme <strong>https://salatjanaza.org</strong> devient un lien ; pour un lien avec
              un texte, écrivez <strong>[voir les prières](https://salatjanaza.org/prieres)</strong>.
            </span>
          </div>

          <BlocPieces
            titre="Images dans le message"
            items={images}
            onAjouter={(fichiers) => ajouterPieces('Image', fichiers)}
            onRetirer={(i) => retirerPiece('Image', i)}
            onInserer={insererMarqueur}
            accept="image/*"
            videTexte="Aucune image. Ajoutez-en une, puis cliquez sur « Insérer ici » pour la placer à l’endroit du curseur dans votre texte."
            avecMarqueur
          />

          <BlocPieces
            titre="Documents joints"
            items={documents}
            onAjouter={(fichiers) => ajouterPieces('Document', fichiers)}
            onRetirer={(i) => retirerPiece('Document', i)}
            videTexte="Aucun document. Ceux que vous ajoutez seront téléchargeables depuis le courriel."
          />

          {erreurPiece && <div className="alert alert-error" role="alert">{erreurPiece}</div>}

          <p className="pieces-mail__poids-total">
            Poids total des pièces : <strong>{Math.round(poidsTotal / 1024).toLocaleString('fr-FR')} Ko</strong> sur 5 120 Ko autorisés.
          </p>
        </SectionMail>

        <div className="message-nouveau__actions">
          {/* LE RÉSULTAT DE L'ENVOI, LÀ OÙ L'ON CLIQUE : dans la barre d'envoi,
              pas hors de vue en haut du formulaire. */}
          {envoye && (
            <div className="admin-confirmation" role="status">
              <span className="admin-confirmation__coche" aria-hidden="true">✓</span>
              <span>
                Message envoyé à <strong>{envoye.adresse}</strong>.
                {envoye.copieDeposee === false && (
                  <span className="admin-confirmation__attention">
                    La copie n’a pas pu être rangée dans « Messages envoyés ».
                  </span>
                )}
              </span>
              <button type="button" className="admin-confirmation__fermer" aria-label="Fermer" onClick={() => setEnvoye(null)}>×</button>
            </div>
          )}
          {erreur && !envoye && (
            <div className="admin-confirmation admin-confirmation--erreur" role="alert">
              <span className="admin-confirmation__coche" aria-hidden="true">!</span>
              <span>{erreur}</span>
              <button type="button" className="admin-confirmation__fermer" aria-label="Fermer" onClick={() => setErreur(null)}>×</button>
            </div>
          )}

          {etatBrouillon && (
            <span
              className={`message-nouveau__brouillon${etatBrouillon.etat === 'erreur' ? ' est-en-erreur' : ''}`}
              role="status"
            >
              {etatBrouillon.etat === 'enregistrement' && 'Enregistrement du brouillon…'}
              {etatBrouillon.etat === 'enregistre' && (
                <>
                  Brouillon enregistré à {etatBrouillon.heure.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  {' '}· Mails › Brouillons
                </>
              )}
              {etatBrouillon.etat === 'erreur' && (etatBrouillon.message ?? 'Le brouillon n’a pas pu être enregistré.')}
            </span>
          )}

          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={!contenuPret}
            onClick={voirApercu}
          >
            Voir l’aperçu
          </button>

          {confirme ? (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm message-nouveau__envoi"
                disabled={envoi}
                onClick={envoyer}
              >
                {envoi ? 'Envoi…' : `Confirmer l’envoi à ${destinataire.trim()}${nombreCopies > 0 ? ` (+ ${nombreCopies} en copie)` : ''}`}
              </button>

              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setConfirme(false)}
              >
                Annuler
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm message-nouveau__envoi"
              disabled={!pret}
              onClick={() => setConfirme(true)}
            >
              Envoyer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
